import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { inboxKeys, useMarkAsRead } from "../api/hooks";
import { useInboxToken } from "../api/useInboxToken";
import { useInboxWebSocket } from "../api/useInboxWebSocket";
import type { ConversationResponse } from "../types";

interface UseConversationSessionParams {
  conversationId?: string;
  /**
   * CRM contact the conversation belongs to. When given, marking the thread
   * read also zeroes the by-contact cache `useConversationByContact` reads, so
   * the unread badges on the lead row / applicants table / application header
   * reset immediately instead of waiting for their poll.
   */
  contactId?: number;
  enabled?: boolean;
  /** Fired for a `new_message` event on THIS conversation only. */
  onMessage?: () => void;
}

/**
 * One conversation's live session: the inbox auth token, the websocket
 * connection, membership of the conversation's socket room, and the read
 * receipt. Shared by the Inbox page and the floating message pop-up so the
 * pop-up does not re-derive any of it.
 *
 * The socket is mounted as soon as a token exists — deliberately NOT gated on
 * `conversationId` — because the Inbox's conversation list has to stay live
 * while nothing is selected. Gate the whole hook with `enabled` instead (the
 * pop-up passes `enabled: isOpen`).
 */
export const useConversationSession = ({
  conversationId,
  contactId,
  enabled = true,
  onMessage
}: UseConversationSessionParams) => {
  const queryClient = useQueryClient();
  const { accessToken, isLoading: isTokenLoading } = useInboxToken();
  const { mutate: markAsReadMutation } = useMarkAsRead();

  const markAsRead = useCallback(
    (id: string) => {
      markAsReadMutation(id, {
        onSuccess: () => {
          if (contactId === undefined) return;
          queryClient.setQueryData<ConversationResponse | null>(
            [...inboxKeys.contacts(), contactId, "CONVERSATION"],
            (old) =>
              old
                ? { conversation: { ...old.conversation, unread_count: 0 } }
                : old
          );
        }
      });
    },
    [markAsReadMutation, contactId, queryClient]
  );

  // Inline callbacks are safe here: useInboxWebSocket keeps them on refs, so a
  // fresh closure per render never tears the connection down.
  const { isConnected, joinConversation, leaveConversation } =
    useInboxWebSocket({
      accessToken,
      enabled: enabled && !!accessToken,
      onNewMessage: (data) => {
        if (!conversationId || data.conversation_id !== conversationId) return;
        markAsRead(conversationId);
        onMessage?.();
      }
    });

  const joinedRef = useRef<string | undefined>(undefined);

  // Join on connect and whenever the conversation changes, and mark the thread
  // read the moment it is on screen. Leaving the previous room matters for the
  // pop-up: its header dropdown swaps applicants without unmounting, and the
  // socket would otherwise stay subscribed to the thread we navigated away
  // from.
  useEffect(() => {
    if (!isConnected || !conversationId) return;
    if (joinedRef.current && joinedRef.current !== conversationId)
      leaveConversation(joinedRef.current);

    joinedRef.current = conversationId;
    joinConversation(conversationId);
    markAsRead(conversationId);
  }, [
    isConnected,
    conversationId,
    joinConversation,
    leaveConversation,
    markAsRead
  ]);

  return {
    accessToken,
    isTokenLoading,
    isConnected,
    joinConversation,
    leaveConversation,
    markAsRead
  };
};
